import React, {useEffect} from 'react';
import {v4 as uuid} from 'uuid';
import {Icon} from '@lightricks/react-design-system';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import {SOURCE_NAMES} from '@/lib/analytics/analyticsConstants';
import {buttonNames, screenNames} from '@/lib/delta/deltaConstants.js';
import calculateAge from '@/utils/calculateAge';
import translate from '@/utils/translate';
import type CreatorGroupMembership from '@/types/creatorGroupMembership';
import CreatorAction from '@/views/creators/components/creator-action/CreatorAction';
import CreatorsActionsBar from '@/views/creators/components/creators-actions-bar/CreatorsActionsBar';
import FloatingPopperBar from '@/components/floating-popper-bar';
import {useBannerStore} from '@/contexts/BannerContext';
import useListCreatorsStore, {
  listCreatorsActions,
} from '@/stores/list-creators/listCreatorsStore';
import useActionItems from '../hooks/useActionItems';
import styles from './ListCreatorsActionBar.module.scss';

const TRANSLATION_PREFIX = 'views.creators.components.creator-action';

function ListCreatorsActionBar({children}: {children: React.ReactNode}) {
  const activeCreatorAction = useListCreatorsStore(
    (state) => state.activeCreatorAction
  );

  const selectedCreators = useListCreatorsStore(
    (state) => state.selectedCreators
  );

  const {dispatch} = useBannerStore();

  useEffect(() => {
    return () => {
      listCreatorsActions.deselectAllCreators();
    };
  }, []);

  const hasProfile = (membership: CreatorGroupMembership) =>
    membership.profile !== undefined;

  const selectedCreatorsProps = [...selectedCreators.values()]
    .filter(hasProfile)
    .map(({profile}) => ({
      id: profile.id,
      name: profile.displayName,
      avatar: profile.imageUrl,
      age: calculateAge(profile.birthDate),
      location: profile.location,
    }));

  const avatarUrls = React.useMemo(() => {
    return Array.from(selectedCreators.values())
      ?.filter(hasProfile)
      .map((creator: CreatorGroupMembership) => creator.profile.imageUrl);
  }, [selectedCreators]);

  const actionItems = useActionItems();

  const getSuccessMessage = () => {
    if (activeCreatorAction === 'remove-from-my-creators') {
      return translate(`${TRANSLATION_PREFIX}.remove-creator.success-title`, {
        count: selectedCreators.size,
      });
    }

    return translate(`${TRANSLATION_PREFIX}.success-title`);
  };

  const handleActionSubmit = (
    reason: 'success' | 'cancelled',
    actionName: string | null,
    creatorIds: string[]
  ) => {
    const creatorIdsObject = creatorIds.reduce(
      (acc: Record<string, string>, creatorId) => {
        acc[creatorId] = creatorId;
        return acc;
      },
      {}
    );
    if (reason === 'success') {
      const eventPayload = {
        screen_presentation_id: uuid(),
        action_name: actionName,
        creator_ids: creatorIdsObject,
        flow_name: actionName?.replace('-', ' ').toLowerCase(),
        campaign_id: '',
        reason: 'success',
        source: SOURCE_NAMES.MY_CREATORS,
        error: null,
      };
      AnalyticsService.dispatchEvent(
        eventNames.MY_CREATORS_ACTION_FLOW_ENDED,
        eventPayload
      );
      dispatch({
        type: 'SET_BANNER',
        title: '',
        message: getSuccessMessage(),
        severity: 'success',
        icon: (
          <Icon
            size="medium"
            appearance="brand"
            name="Actions-Patch-Accept-Line"
          />
        ),
      });
    } else if (reason === 'cancelled') {
      const eventPayload = {
        screen_presentation_id: uuid(),
        action_name: actionName,
        creator_ids: creatorIdsObject,
        flow_name: actionName?.replace('-', ' ').toLowerCase(),
        campaign_id: '',
        reason: 'cancelled',
        source: SOURCE_NAMES.MY_CREATORS,
        error: null,
      };
      AnalyticsService.dispatchEvent(
        eventNames.MY_CREATORS_ACTION_FLOW_ENDED,
        eventPayload
      );
    }

    listCreatorsActions.setActiveCreatorAction(null);
  };

  return (
    <FloatingPopperBar
      isOpen={selectedCreators.size > 0}
      placement="bottom"
      className={styles.container}
      renderContent={() => (
        <CreatorsActionsBar
          avatarUrls={avatarUrls}
          actionItems={actionItems}
          closeButtonProps={{
            appearance: 'neutral',
            size: 'medium',
            mode: 'plain',
          }}
          closeButtonText="Clear selection"
          closeButtonOnClick={() => {
            listCreatorsActions.deselectAllCreators();

            AnalyticsService.dispatchEvent(eventNames.BUTTON_PRESSED, {
              button_name: buttonNames.MY_CREATORS.CLEAR_SELECTION,
              creator_id: selectedCreatorsProps.map((c) => c.id).join(','),
              screen_name: screenNames.MY_CREATORS.MY_CREATORS,
              flow_id: '',
              flow_name: '',
              triggered_flow_id: '',
              triggered_flow_name: '',
              screen_presentation_id: uuid(),
              tab: '',
              campaign_id: '',
            });
          }}
        />
      )}
    >
      <CreatorAction
        activeCreatorAction={activeCreatorAction}
        creators={selectedCreatorsProps}
        close={(reason) => {
          listCreatorsActions.setActiveCreatorAction(null);

          handleActionSubmit(
            reason,
            activeCreatorAction,
            selectedCreatorsProps.map((c) => c.id)
          );
        }}
      />
      {children}
    </FloatingPopperBar>
  );
}

export default ListCreatorsActionBar;
